import { useFormik } from 'formik'
import FormInput from '../../components/Input'
import PageWrapper from '../../components/PageWrapper'
import DefaultButton from '../../components/Button/Button'
import settings from '../../assets/icons/navigate-settings.svg'
import { Link } from 'react-router-dom'
import { changePasswordSchema } from '../../validations/changePassword'
import { showToast } from '../../utils/toast'
import { useState } from 'react'
import StaffService from '../../api/staff-service'

interface ChangePasswordFormValues {
  oldPassword: string;
  newPassword: string;
}

const ChangePassword = () => {
  const [loading, setLoading] = useState(false)

  const formik = useFormik<ChangePasswordFormValues>({
    initialValues: {
      oldPassword: '',
      newPassword: '',
    },
    validationSchema: changePasswordSchema,
    onSubmit: (values) => {
      if (loading) return

      setLoading(true)

      StaffService.updateStaffPassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      })
        .then(() => {
          showToast({
            id: 'change-password-success',
            title: 'Password Changed Successfully',
            description: 'Your password has been updated.',
            status: 'success',
          })
          formik.resetForm()
        })
        .catch((error) => {
          const errorMessage = error?.errors?.newPassword || error?.message
          showToast({
            id: 'change-password-error',
            title: 'Error Changing Password',
            description: errorMessage || 'Invalid input. Please check your entries.',
            status: 'error',
          })
          console.error('Error:', error)
        })
        .finally(() => {
          setLoading(false)
        })
    },
  })

  return (
    <PageWrapper title="Settings" description="Password and Account settings">
      <div className="space-y-6 sm:space-y-8 md:space-y-20 px-3 sm:px-4 md:px-0">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6 md:gap-10">
          <Link 
            to="/settings" 
            className="flex items-center gap-2 sm:gap-3 group hover:opacity-80 transition-opacity"
          >
            <img 
              src={settings} 
              alt="back-arrow" 
              className="w-5 h-5 sm:w-6 sm:h-6" 
            />
            <p className="font-medium text-base sm:text-lg md:text-xl text-gray-700 group-hover:text-gray-900">
              Settings
            </p>
          </Link>
          <div className="hidden sm:block w-full sm:w-auto bg-gray-900 px-4 sm:px-6 py-2.5 sm:py-2 rounded-lg text-center cursor-default">
            <p className="font-medium text-base sm:text-lg md:text-lg text-white">
              Change Password
            </p>
          </div>

        </div>

        {/* Main Form Container */}
        <div className="w-full max-w-full sm:max-w-[656px] mx-auto">
          <p className="font-medium text-xl sm:text-2xl md:text-3xl mb-4 sm:mb-6 md:mb-8">
            Change Password
          </p>
          
          <form onSubmit={formik.handleSubmit} className="space-y-6 sm:space-y-8">
            <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-10">
              <div className="flex-1 w-full">
                <FormInput
                  {...formik.getFieldProps('oldPassword')}
                  placeholder="Enter old Password"
                  label="Old Password"
                  type="password"
                  useToggle={true}
                  error={formik.touched.oldPassword ? formik.errors.oldPassword : undefined}
                  focusBorderColor="transparent"
                  className="w-full"
                />
              </div>
              <div className="flex-1 w-full">
                <FormInput
                  {...formik.getFieldProps('newPassword')}
                  placeholder="Enter new Password"
                  label="New Password"
                  type="password"
                  useToggle={true}
                  error={formik.touched.newPassword ? formik.errors.newPassword : undefined}
                  focusBorderColor="transparent"
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4 sm:mt-6">
              <DefaultButton
                type="submit"
                text="Change Password"
                isLoading={loading}
                loadingText="Updating..."
                disabled={loading}
                background="gray.900"
                color="white"
                className="w-full sm:w-auto min-w-[200px]"
              />
            </div>
          </form>
        </div>
      </div>
    </PageWrapper>
  )
}

export default ChangePassword
